import { inject, Injectable, Injector } from '@angular/core';
import { ERROR_CODE, ERROR_STATUS, IErrorResponse } from '../models/error.models';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoadingService } from 'src/app/core/services/loading.service';
import { MediaServerRestartService } from './media-server-restart.service';
import { ProcessService } from 'src/app/user-identification/services/process.service';

@Injectable({
	providedIn: 'root',
})
export class ErrorService {
	private loadingService = inject(LoadingService);
	private mediaServerRestartService = inject(MediaServerRestartService);
	/** Lazy: ProcessService inyecta ErrorService (evitar ciclo DI). */
	private injector = inject(Injector);
	private errorGeneral: BehaviorSubject<IErrorResponse> = new BehaviorSubject<IErrorResponse>(null);
	public errorGeneral$: Observable<IErrorResponse> = this.errorGeneral.asObservable();

	public hasGeneralError(error: IErrorResponse): boolean {
		const code = error?.error?.error_code?.toString();
		// 10xx acceso/sesión; 3099 sistema/proveedor caído (NIF-944) — overlay global, no pageWarning de op.
		const hasGeneralError: boolean =
			!!code && (code.startsWith('10') || code === ERROR_CODE.SYSTEM_UNAVAILABLE);
		if (hasGeneralError) {
			this.setErrorGeneral(error);
			this.loadingService.hide();
			// Trigger único: cualquier op que muestre 3099 cierra el proceso vía ProcessService.
			if (code === ERROR_CODE.SYSTEM_UNAVAILABLE) {
				this.injector.get(ProcessService).finalizeOnSystemUnavailable();
			}
		}
		return hasGeneralError;
	}

	/**
	 * Muestra la pantalla de error de streaming con botón de reintento.
	 * Al pulsar RETRY: waiting → notifica restart al back → ejecuta action.
	 * @param action Callback que se ejecuta al reintentar el streaming
	 */
	public setStreamingError(action: () => void | Promise<void>): void {
		const mediaError = (waiting: boolean, retryAction: () => void | Promise<void>): IErrorResponse => ({
			code: ERROR_STATUS.DATA,
			error: { error_code: ERROR_CODE.MEDIA_SERVER_ERROR, msg: '' },
			action: retryAction,
			waiting,
		});

		const wrapped = async (): Promise<void> => {
			this.setErrorGeneral(mediaError(true, wrapped));
			this.mediaServerRestartService.notifyMediaServerFailure('streaming_ui_retry');
			try {
				await action();
				// Action OK = streaming recuperado. Limpiar MEDIA_SERVER aunque un setStreamingError
				// anidado (carrera al remontar OCR) haya dejado waiting=false con botón.
				const current = this.errorGeneral.getValue();
				const shouldClear =
					!current ||
					current.error?.error_code === ERROR_CODE.MEDIA_SERVER_ERROR ||
					current.waiting;
				if (shouldClear) {
					this.clearError();
				}
			} catch {
				this.setErrorGeneral(mediaError(false, wrapped));
			}
		};

		this.setErrorGeneral(mediaError(false, wrapped));
	}

	/**
	 * Pantalla disuasoria por DevTools abiertas. No pisa errores de acceso (10xx).
	 * @param action Callback del botón Continuar (p. ej. solo limpiar si DevTools ya cerradas).
	 */
	public setDevToolsBlockedError(action: () => void): void {
		const current = this.errorGeneral.getValue();
		if (current?.error?.error_code?.toString().startsWith('10')) {
			return;
		}
		this.setErrorGeneral({
			code: ERROR_STATUS.DATA,
			error: { error_code: ERROR_CODE.DEVTOOLS_BLOCKED, msg: '' },
			action,
		});
	}

	/** True si el overlay actual es el de DevTools bloqueadas. */
	public isDevToolsBlockedError(): boolean {
		return this.errorGeneral.getValue()?.error?.error_code === ERROR_CODE.DEVTOOLS_BLOCKED;
	}

	/**
	 * Limpia errores transitorios al recargar config, sin quitar el bloqueo por DevTools.
	 */
	public clearErrorUnlessDevToolsBlocked(): void {
		if (this.isDevToolsBlockedError()) {
			return;
		}
		this.clearError();
	}

	/** Limpia el error general (permite volver al flujo normal) */
	public clearError(): void {
		this.errorGeneral.next(null);
	}

	public getErrorGeneral(): Observable<IErrorResponse> {
		return this.errorGeneral.asObservable();
	}

	public setErrorGeneral(value: IErrorResponse): void {
		this.errorGeneral.next(value);
	}
}
